import { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button, CustomSelect, Pagination, SearchInput } from '@/components';
import { formatName, parseApiError } from '@/common/utils';
import { useOutsideClick } from '@/common/hooks';
import { getContacts, getCustomerTypes } from '../api';
import { getCRMContacts, evaluateCRMSegment } from '../crm/api';
import { createGmailCampaign } from '../api';
import {
  DashboardTable,
  DashboardTableHeader,
  DashboardTableBody,
  DashboardTableRow,
  DashboardTableCell,
} from './PremiumDesign';
import { FilterIcon } from './Icons';
import { EmailPhonePreview } from './EmailPhonePreview';
import emailTemplateIcon from '../assets/userTemplate.png'; // Using same icon

const autoFillableKeys = ['name', 'first_name', 'last_name', 'email', 'phone'];
const imageKeys = ['header_image', 'image'];

const DYNAMIC_VARIABLE_SUGGESTIONS = [
  { key: 'name', label: 'Customer Name' },
  { key: 'first_name', label: 'First Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'company_name', label: 'Company Name' },
];

export function SendEmailCampaignPage({
  companyId,
  template,
  segment = null,
  onCancel,
  onSend,
  className = '',
}) {
  const [campaignName, setCampaignName] = useState(
    segment?.name
      ? `${segment.name} - ${template?.name || 'Campaign'}`
      : `Campaign - ${template?.name || ''}`,
  );
  const [subject, setSubject] = useState(template?.subject || '');

  // Variables logic
  const variables = useMemo(() => {
    const matches = new Set();
    const regex = /{{(.*?)}}/g;
    let match;
    const bodyStr = template?.body || '';
    while ((match = regex.exec(bodyStr)) !== null) {
      matches.add(match[1].trim());
    }
    const subjectStr = subject || '';
    while ((match = regex.exec(subjectStr)) !== null) {
      matches.add(match[1].trim());
    }
    return Array.from(matches);
  }, [template, subject]);

  const manualVariables = useMemo(() => {
    return variables.filter(
      (v) =>
        !autoFillableKeys.includes(v.toLowerCase()) &&
        !imageKeys.includes(v.toLowerCase()),
    );
  }, [variables]);

  const autoVariables = useMemo(() => {
    return variables.filter((v) => autoFillableKeys.includes(v.toLowerCase()));
  }, [variables]);

  const hasImageVariable = useMemo(() => {
    return variables.some((v) => imageKeys.includes(v.toLowerCase()));
  }, [variables]);

  const [variableValues, setVariableValues] = useState({});
  const [mediaFile, setMediaFile] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);

  const isImageReady = !hasImageVariable || Boolean(mediaFile);
  const areManualVariablesFilled = useMemo(() => {
    if (!manualVariables.length) return true;
    return manualVariables.every((v) => Boolean(variableValues[v]?.trim()));
  }, [manualVariables, variableValues]);
  const isEmailTemplateReady = isImageReady && areManualVariablesFilled;

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [activeFilterFields, setActiveFilterFields] = useState([]);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const filterMenuRef = useRef(null);
  const [page, setPage] = useState(1);
  const [selectedContacts, setSelectedContacts] = useState(new Map());
  const [isSelectingAll, setIsSelectingAll] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useOutsideClick(
    filterMenuRef,
    () => setIsFilterMenuOpen(false),
    isFilterMenuOpen,
  );

  const buildContactParams = (overrides = {}) => {
    if (segment?.id) {
      const params = { segment: String(segment.id) };
      if (debouncedSearch) params.search = debouncedSearch;
      return { ...params, ...overrides };
    }
    const params = { has_email: true };
    if (debouncedSearch) params.search = debouncedSearch;
    if (typeFilter) params.filter = typeFilter;
    return { ...params, ...overrides };
  };

  const { data: contactsData, isLoading: isContactsLoading } = useQuery({
    queryKey: [
      'email-send-contacts',
      companyId,
      debouncedSearch,
      page,
      typeFilter,
      segment?.id,
      'has_email_only',
    ],
    queryFn: async () => {
      if (segment?.id) {
        try {
          const res = await getCRMContacts(
            companyId,
            buildContactParams(page > 1 ? { page } : {}),
          );
          if (res?.results && res.results.length > 0) {
            return res;
          }
          if (
            segment.sample_contacts &&
            segment.sample_contacts.length > 0 &&
            !debouncedSearch
          ) {
            return {
              count: segment.sample_contacts.length,
              results: segment.sample_contacts,
            };
          }
          const evalRes = await evaluateCRMSegment({
            companyId,
            segmentId: segment.id,
            payload: {},
          });
          if (evalRes?.sample_contacts && evalRes.sample_contacts.length > 0) {
            return {
              count: evalRes.count || evalRes.sample_contacts.length,
              results: evalRes.sample_contacts,
            };
          }
        } catch (err) {
          console.error('Error fetching CRM segment contacts:', err);
          if (segment.sample_contacts) {
            return {
              count: segment.sample_contacts.length,
              results: segment.sample_contacts,
            };
          }
        }
      }
      return getContacts(
        companyId,
        buildContactParams(page > 1 ? { page } : {}),
      );
    },
    enabled: !!companyId,
  });

  const { data: customerTypesData } = useQuery({
    queryKey: ['customer-types', companyId],
    queryFn: () => getCustomerTypes(companyId),
    enabled: !!companyId,
  });
  const customerTypes = customerTypesData || [];
  const typeFilterOptions = [
    { value: '', label: 'All Type' },
    ...customerTypes.map((t) => ({ value: t.name, label: t.name })),
  ];

  const contacts = useMemo(() => {
    return (contactsData?.results || []).filter((c) => {
      const email = (c.email || '').trim();
      return email.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    });
  }, [contactsData]);
  const count = contactsData?.count || 0;

  const hasInitializedSegmentSelection = useRef(false);
  useEffect(() => {
    if (
      segment &&
      contacts.length > 0 &&
      !hasInitializedSegmentSelection.current
    ) {
      hasInitializedSegmentSelection.current = true;
      const map = new Map();
      contacts.forEach((c) => {
        const email = (c.email || '').trim();
        if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          map.set(c.id, c);
        }
      });
      setSelectedContacts(map);
    }
  }, [segment, contacts]);

  const handleAddFilterField = () => {
    setActiveFilterFields(['customerType']);
    setIsFilterMenuOpen(false);
  };

  const handleRemoveFilterField = () => {
    setActiveFilterFields([]);
    setTypeFilter('');
    setPage(1);
    setSelectedContacts(new Map());
  };

  const handleClearAllFilters = () => {
    setTypeFilter('');
    setActiveFilterFields([]);
    setPage(1);
    setSelectedContacts(new Map());
  };

  const toggleSelection = (contact) => {
    const email = (contact.email || '').trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error('This contact does not have a valid email address.');
      return;
    }
    setSelectedContacts((prev) => {
      const next = new Map(prev);
      if (next.has(contact.id)) {
        next.delete(contact.id);
      } else {
        next.set(contact.id, contact);
      }
      return next;
    });
  };

  const isAllSelected =
    contacts.length > 0 && selectedContacts.size >= contacts.length;

  const toggleAllSelection = async () => {
    if (isSelectingAll || count === 0) return;

    if (isAllSelected) {
      setSelectedContacts(new Map());
      return;
    }

    setIsSelectingAll(true);
    const loadingToast = toast.loading('Selecting all contacts...');
    try {
      let allMap = new Map();
      let currentPage = 1;
      let hasMore = true;
      while (hasMore) {
        let res;
        if (segment?.id) {
          try {
            res = await getCRMContacts(
              companyId,
              buildContactParams({ page: currentPage }),
            );
          } catch {
            res = null;
          }
          if (!res?.results?.length && currentPage === 1) {
            const evalRes = await evaluateCRMSegment({
              companyId,
              segmentId: segment.id,
              payload: {},
            });
            res = {
              results:
                evalRes?.sample_contacts || segment.sample_contacts || [],
            };
          }
        } else {
          res = await getContacts(
            companyId,
            buildContactParams({ page: currentPage }),
          );
        }

        if (res && res.results && res.results.length > 0) {
          res.results.forEach((c) => {
            const email = (c.email || '').trim();
            if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
              allMap.set(c.id, c);
            }
          });
          currentPage = res.next ? currentPage + 1 : currentPage;
          hasMore = !!res.next;
        } else {
          hasMore = false;
        }
      }
      setSelectedContacts(allMap);
      toast.success(`Selected all ${allMap.size} contacts!`, {
        id: loadingToast,
      });
    } catch {
      toast.error('Failed to select all contacts.', { id: loadingToast });
    } finally {
      setIsSelectingAll(false);
    }
  };

  const sendMutation = useMutation({
    mutationFn: (payload) => createGmailCampaign({ companyId, payload }),
    onSuccess: (data) => {
      toast.success('Email campaign sent successfully!');
      onSend?.(data);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to send campaign.');
    },
  });

  const handleSend = async () => {
    if (!campaignName.trim()) {
      toast.error('Campaign name is required.');
      return;
    }
    if (!isEmailTemplateReady) {
      return;
    }
    if (selectedContacts.size === 0) {
      toast.error('Please select at least one contact to send messages to.');
      return;
    }

    const recipients = [];
    for (const contact of selectedContacts.values()) {
      const email = contact.email || '';
      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        toast.error(
          `Invalid email for contact: ${contact.name || contact.phone}`,
        );
        return;
      }

      const contactVars = { ...variableValues };

      for (const v of autoVariables) {
        const lower = v.toLowerCase();
        if (lower === 'name') contactVars[v] = contact.name || '';
        if (lower === 'first_name')
          contactVars[v] = (contact.name || '').split(' ')[0] || '';
        if (lower === 'last_name')
          contactVars[v] =
            (contact.name || '').split(' ').slice(1).join(' ') || '';
        if (lower === 'email') contactVars[v] = email;
        if (lower === 'phone') contactVars[v] = contact.phone || '';
      }

      // Also resolve dynamic tags inside manual variables per recipient
      Object.entries(contactVars).forEach(([k, val]) => {
        if (typeof val === 'string' && val.includes('{{')) {
          contactVars[k] = val
            .replace(/{{\s*(?:customer_name|name)\s*}}/gi, contact.name || '')
            .replace(
              /{{\s*first_name\s*}}/gi,
              (contact.name || '').split(' ')[0] || '',
            )
            .replace(
              /{{\s*last_name\s*}}/gi,
              (contact.name || '').split(' ').slice(1).join(' ') || '',
            )
            .replace(/{{\s*email\s*}}/gi, email)
            .replace(/{{\s*phone\s*}}/gi, contact.phone || '')
            .replace(/{{\s*company_name\s*}}/gi, contact.company_name || '');
        }
      });

      recipients.push({
        email: email,
        name: contact.name || '',
        variables: contactVars,
      });
    }

    let inline_image_base64 = null;
    let inline_image_filename = null;
    if (mediaFile) {
      inline_image_base64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.readAsDataURL(mediaFile);
      });
      inline_image_filename = mediaFile.name;
    }

    sendMutation.mutate({
      campaign_name: campaignName,
      template_id: template.id,
      subject: subject,
      body: template.body,
      recipients,
      inline_image_base64,
      inline_image_filename,
    });
  };

  const isSending = sendMutation.isPending;

  return (
    <div
      className={`email-send flex flex-col gap-5 w-full mx-auto flex-1 animate-fadeIn relative ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2 sm:gap-3 w-full shrink-0 min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            type="button"
            onClick={onCancel}
            className="w-9 h-9 rounded-[8px] bg-white border border-primary text-primary hover:bg-surface-mint flex items-center justify-center transition-colors focus:outline-none shrink-0"
            aria-label="Back"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div className="flex flex-col min-w-0">
            <h1 className="text-base sm:text-xl font-bold text-main-black truncate shrink min-w-0 max-w-xs sm:max-w-md">
              {template?.name || 'New Email Campaign'}
            </h1>
            {segment?.name && (
              <p className="text-xs text-text-secondary truncate">
                Group:{' '}
                <span className="text-primary font-semibold">
                  {segment.name}
                </span>
              </p>
            )}
          </div>
        </div>

        {/* Recipients Info in Header */}
        <div className="flex items-center gap-2 sm:gap-3 bg-surface-mint px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-xl border border-primary/25 shrink-0">
          <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0 bg-[#50AB3D] text-white">
            <img
              src={emailTemplateIcon}
              alt=""
              className="w-3.5 h-3.5 sm:w-4 sm:h-4 object-contain brightness-0 invert"
            />
          </div>
          <div className="flex flex-col min-w-0">
            <span className="text-[10px] sm:text-[11px] font-medium leading-[1] text-text-secondary">
              Recipients
            </span>
            <span className="text-xs sm:text-[14px] font-bold leading-[1.2] text-main-black mt-0.5 sm:mt-1 truncate">
              {selectedContacts.size > 0
                ? `${selectedContacts.size} Selected`
                : 'None Selected'}
            </span>
          </div>
        </div>
      </div>

      {/* Two column area */}
      <div className="flex flex-col lg:flex-row flex-1 gap-5 w-full items-start">
        {/* LEFT: fills parent slot */}
        <div className="email-send-form w-full lg:flex-[7] lg:basis-0 lg:min-w-0 flex flex-col gap-4 bg-form-background-light rounded-2xl border border-surface-mint p-4">
          <div className="flex flex-col gap-[10px] animate-fadeIn shrink-0">
            <h3 className="text-[18px] leading-[26px] font-medium text-primary font-sf-pro-display">
              Campaign Details
            </h3>
            <div className="bg-white rounded-[12px] p-4 flex flex-col gap-4 w-full">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-text-secondary">
                    Campaign Name <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="e.g. September Newsletter"
                    className="w-full h-11 px-3.5 rounded-xl bg-white border border-border-default text-main-black text-sm focus:outline-none focus:border-primary transition-all"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-text-secondary">
                    Email Subject
                  </label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="Email Subject"
                    className="w-full h-11 px-3.5 rounded-xl bg-white border border-border-default text-main-black text-sm focus:outline-none focus:border-primary transition-all"
                  />
                </div>
              </div>

              {variables.length > 0 && (
                <div className="email-send-vars mt-2 border-t border-gray-100 pt-4">
                  <label className="text-xs font-semibold text-text-secondary mb-2 block">
                    Template Variables{' '}
                    <span className="text-primary">({variables.length})</span>
                  </label>

                  {autoVariables.length > 0 && (
                    <div className="mb-4 bg-surface-mint/30 p-3 rounded-lg border border-primary/20 flex flex-col gap-2">
                      <p className="text-xs text-primary font-medium flex items-center gap-1.5">
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        These variables will be automatically filled for each
                        contact:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {autoVariables.map((v) => (
                          <span
                            key={v}
                            className="bg-white border border-primary/30 text-primary text-[11px] font-semibold px-2 py-1 rounded-md"
                          >
                            {'{'}
                            {'{'} {v} {'}'}
                            {'}'}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasImageVariable && (
                    <div className="mb-4 flex flex-col gap-2 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <label className="text-xs font-semibold text-text-secondary flex items-center gap-2">
                        <svg
                          className="w-4 h-4 text-primary"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        Header Image ({'{'}
                        {'{'}header_image{'}'}
                        {'}'} / {'{'}
                        {'{'}image{'}'}
                        {'}'})
                      </label>
                      <div className="relative mt-1">
                        <input
                          id="email-media-upload"
                          type="file"
                          className="sr-only"
                          accept="image/*"
                          disabled={isCompressing}
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) {
                              setMediaFile(null);
                              return;
                            }
                            try {
                              setIsCompressing(true);
                              const formData = new FormData();
                              formData.append('image', file);
                              const response = await axios.post(
                                'https://compress.flexioninfotech.site/api/compress',
                                formData,
                                {
                                  headers: {
                                    'Content-Type': 'multipart/form-data',
                                  },
                                  responseType: 'blob',
                                },
                              );
                              const blob = response.data;
                              const compressedFile = new File(
                                [blob],
                                file.name,
                                { type: blob.type || 'image/jpeg' },
                              );
                              setMediaFile(compressedFile);
                              toast.success('Image compressed and selected.');
                            } catch (error) {
                              console.error('Image compression failed:', error);
                              setMediaFile(file);
                            } finally {
                              setIsCompressing(false);
                            }
                          }}
                        />
                        <label
                          htmlFor="email-media-upload"
                          className={`inline-flex items-center px-4 py-2 rounded-lg border border-border-default text-sm font-semibold transition-all ${
                            isCompressing
                              ? 'bg-form-background-light text-text-muted cursor-not-allowed opacity-50'
                              : 'bg-white text-main-black hover:bg-surface-mint cursor-pointer'
                          }`}
                        >
                          <span className="truncate max-w-[200px]">
                            {mediaFile ? mediaFile.name : 'Choose Image'}
                          </span>
                        </label>
                      </div>
                      {!mediaFile && (
                        <p className="text-xs text-red-500 font-medium mt-1">
                          Header image is required.
                        </p>
                      )}
                      {isCompressing && (
                        <div className="flex items-center gap-2 mt-1">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary border-t-transparent"></div>
                          <span className="text-xs text-text-secondary font-bold">
                            Compressing image...
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {manualVariables.length > 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-gray-50/50 p-3 border border-gray-100 rounded-xl">
                      {manualVariables.map((v) => (
                        <div key={v} className="flex flex-col gap-1.5">
                          <label className="text-xs font-semibold text-text-secondary">
                            {'{'}
                            {'{'} {v} {'}'}
                            {'}'} <span className="text-primary">*</span>
                          </label>
                          <div className="flex flex-wrap gap-1">
                            {DYNAMIC_VARIABLE_SUGGESTIONS.map((sug) => (
                              <button
                                key={sug.key}
                                type="button"
                                onClick={() =>
                                  setVariableValues((prev) => {
                                    const current = prev[v] || '';
                                    const tag = `{{${sug.key}}}`;
                                    const next =
                                      current && !/\s$/.test(current)
                                        ? `${current} ${tag}`
                                        : `${current}${tag}`;
                                    return { ...prev, [v]: next };
                                  })
                                }
                                className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-[#E8F5E9] text-[#1B5E20] hover:bg-[#C8E6C9] transition-colors"
                              >
                                {sug.label}
                              </button>
                            ))}
                          </div>
                          <input
                            type="text"
                            value={variableValues[v] || ''}
                            onChange={(e) =>
                              setVariableValues((prev) => ({
                                ...prev,
                                [v]: e.target.value,
                              }))
                            }
                            placeholder={`Value or click field tag for ${v}`}
                            className="w-full h-10 px-3 rounded-lg bg-white border border-border-default text-main-black text-sm focus:outline-none focus:border-primary transition-all"
                          />
                          {!variableValues[v]?.trim() && (
                            <p className="text-xs text-red-500 font-medium">
                              This variable is required.
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {manualVariables.length === 0 && autoVariables.length > 0 && (
                    <p className="text-xs text-text-muted mt-2">
                      All variables are handled automatically!
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="email-send-recipients flex flex-col gap-3 flex-1 min-h-0 mt-2">
            <div className="email-send-toolbar flex flex-wrap items-center gap-3 shrink-0">
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by Name or Email"
                className="w-full sm:w-auto sm:flex-1 sm:min-w-[220px]"
                inputClassName="!h-11 !bg-white"
              />
              <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto shrink-0">
                <div className="relative shrink-0" ref={filterMenuRef}>
                  <button
                    type="button"
                    onClick={() => setIsFilterMenuOpen((prev) => !prev)}
                    className={`relative h-11 px-4 rounded-[12px] text-sm font-semibold flex items-center gap-2 transition-colors shrink-0 focus:outline-none ${
                      isFilterMenuOpen || activeFilterFields.length > 0
                        ? 'bg-primary text-white hover:bg-primary-hover'
                        : 'bg-[#F5F5F5] text-[#1F2241] hover:brightness-95'
                    }`}
                  >
                    <FilterIcon className="w-4 h-4" />
                    Filter
                  </button>
                  {isFilterMenuOpen && (
                    <div className="absolute left-0 top-[52px] z-40 w-56 bg-white border border-[#EBF4EA] rounded-xl shadow-xl py-2">
                      <p className="px-4 py-1.5 text-xs font-semibold text-text-muted uppercase tracking-wide">
                        Filter by
                      </p>
                      {!activeFilterFields.includes('customerType') && (
                        <button
                          type="button"
                          onClick={handleAddFilterField}
                          className="w-full text-left px-4 py-2 text-sm font-medium text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] transition-colors"
                        >
                          Customer Type
                        </button>
                      )}
                      {activeFilterFields.length > 0 && (
                        <p className="px-4 py-2 text-sm text-text-muted">
                          All filters added
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {activeFilterFields.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                <div className="h-11 flex items-center gap-2 pl-3 pr-2 rounded-xl border border-[#EBF4EA] bg-white shrink-0">
                  <span className="text-sm font-semibold text-[#1F2241] whitespace-nowrap">
                    Customer Type
                  </span>
                  <span className="text-sm text-[#868686]">is</span>
                  <CustomSelect
                    value={typeFilter}
                    onChange={(val) => {
                      setTypeFilter(val);
                      setPage(1);
                      setSelectedContacts(new Map());
                    }}
                    options={typeFilterOptions}
                    dropdownWidth={180}
                    buttonClassName="h-8 px-2 rounded-lg bg-[#F5F5F5] text-sm font-medium text-[#1F2241] flex items-center gap-1 cursor-pointer focus:outline-none"
                    textSizeClass="text-sm"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveFilterField}
                    className="text-[#868686] hover:text-[#1F2241] shrink-0"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="text-sm font-semibold text-[#50AB3D] hover:text-primary-hover shrink-0"
                >
                  Clear all
                </button>
              </div>
            )}

            <div className="email-send-contacts w-full rounded-2xl bg-layer-5-card flex flex-col overflow-hidden flex-1 gap-2 shrink-0 min-h-[420px]">
              <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar">
                <DashboardTable>
                  <DashboardTableHeader>
                    <th className="py-3 px-4 w-12 text-center">
                      <label className="flex items-center justify-center cursor-pointer relative">
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={toggleAllSelection}
                          disabled={isSelectingAll}
                          className="peer sr-only"
                        />
                        <div
                          className={`w-5 h-5 rounded-[6px] border ${isAllSelected || selectedContacts.size > 0 ? 'bg-primary border-primary' : 'border-border-active bg-transparent'} flex items-center justify-center transition-all`}
                        >
                          {isSelectingAll ? (
                            <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : isAllSelected ? (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="3"
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          ) : selectedContacts.size > 0 ? (
                            <svg
                              className="w-3 h-3 text-white"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="3"
                                d="M20 12H4"
                              />
                            </svg>
                          ) : null}
                        </div>
                      </label>
                    </th>
                    <th className="py-3 px-4 text-left">Name</th>
                    <th className="py-3 px-4 text-left min-w-[200px]">Email</th>
                    <th className="py-3 px-4 text-center">Type</th>
                  </DashboardTableHeader>
                  <DashboardTableBody>
                    {isContactsLoading ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="py-16 text-center text-text-muted"
                        >
                          Loading contacts...
                        </td>
                      </tr>
                    ) : contacts.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          className="py-16 text-center text-text-muted"
                        >
                          No contacts found
                        </td>
                      </tr>
                    ) : (
                      contacts.map((contact) => {
                        const email = contact.email || '';
                        return (
                          <DashboardTableRow
                            key={contact.id}
                            onClick={() => toggleSelection(contact)}
                          >
                            <td
                              className="py-4 px-4 text-center"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <label className="flex items-center justify-center cursor-pointer relative">
                                <input
                                  type="checkbox"
                                  checked={selectedContacts.has(contact.id)}
                                  onChange={() => toggleSelection(contact)}
                                  className="peer sr-only"
                                />
                                <div
                                  className={`w-5 h-5 rounded-[6px] border ${selectedContacts.has(contact.id) ? 'bg-primary border-primary' : 'border-border-active bg-transparent'} flex items-center justify-center transition-all`}
                                >
                                  {selectedContacts.has(contact.id) && (
                                    <svg
                                      className="w-3 h-3 text-white"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="3"
                                        d="M5 13l4 4L19 7"
                                      />
                                    </svg>
                                  )}
                                </div>
                              </label>
                            </td>
                            <DashboardTableCell>
                              {formatName(contact.name) || '-'}
                            </DashboardTableCell>
                            <DashboardTableCell>
                              {email || '-'}
                            </DashboardTableCell>
                            <DashboardTableCell align="center">
                              {typeof contact.customer_type === 'object' &&
                              contact.customer_type !== null
                                ? contact.customer_type.name
                                : contact.customer_type || '-'}
                            </DashboardTableCell>
                          </DashboardTableRow>
                        );
                      })
                    )}
                  </DashboardTableBody>
                </DashboardTable>
              </div>
              <Pagination
                currentPage={page}
                totalCount={count}
                itemsPerPage={10}
                onChange={setPage}
                className="bg-white shrink-0"
              />
            </div>
          </div>
        </div>

        {/* RIGHT: Email Preview & Actions */}
        <div className="w-full lg:w-[330px] xl:w-[350px] 2xl:w-[365px] shrink-0 self-start lg:sticky lg:top-0 flex flex-col gap-4">
          <div className="email-send-preview w-full flex flex-col items-center justify-center rounded-2xl border border-surface-mint bg-form-background-light p-3 sm:p-4 min-[1366px]:p-4 overflow-hidden">
            <div className="email-send-phone-slot relative mx-auto w-full">
              <div className="email-send-phone-scale">
                <EmailPhonePreview
                  template={template}
                  subject={subject}
                  body={template?.body}
                  variableValues={variableValues}
                  mediaFile={mediaFile}
                  className="my-auto lg:!w-[330px] xl:!w-[360px] 2xl:!w-[380px] !h-[580px] sm:!h-[620px] xl:!h-[660px]"
                />
              </div>
            </div>
          </div>

          {/* Bottom Action */}
          <div className="email-send-actions grid grid-cols-2 gap-3 w-full shrink-0 mt-auto sm:flex sm:items-center sm:justify-end">
            <Button
              variant="secondary"
              onClick={onCancel}
              className="h-11 px-4 sm:px-6 font-semibold w-full sm:w-auto justify-center"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSend}
              disabled={
                isSending ||
                isCompressing ||
                !isEmailTemplateReady ||
                selectedContacts.size === 0
              }
              className="h-11 px-4 sm:px-8 font-semibold w-full sm:w-auto justify-center"
            >
              {isSending ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
